import React from 'react';
import { useEffect, useState } from 'react';
import { IFrameRefHandler } from '../../modules/Common/IFrameRefHandler';
import { deleteHotspotConfig, viewHotspotConfig } from '../../modules/hotspotUtils/ViewerConfig';
import { useAuthStore } from '../../stores/authStore';
import { useCommonStore } from '../../stores/commonStore';
import { useRequestorStore } from '../../stores/requestorStore';
import { IhostspotResponse } from '../../types/hotspot';
import { deleteFeedback, deleteHotspot, fetchHotspotData, getFeedback, resolveFeedback } from '../../utils/api/feedbackApi';
import { Messages, MessageType, Roles } from '../../utils/enums';

interface BannerProps {
    name: string;
    dateAndtime: string | undefined;
    text: string;
    isHighlighted?: boolean | undefined;
    feedbackId?: string;
    hotspotId?: string;
    isResolved?: boolean;
}

const ToolBar = ({ feedbackId, isResolved, hotspotId }) => {
    const [showMenu, setShowMenu] = useState(false);
    const setToastMessage = useCommonStore((state) => state.setToastMessage);
    const setFlyoutRightOpen = useCommonStore((state) => state.setFlyoutRightOpen);
    const setProductFlagged = useCommonStore((state) => state.setProductFlagged);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const setFeedback = useRequestorStore((state) => state.setFeedback);
    const activeProduct = useRequestorStore((state) => state.activeProduct);
    const setActiveHotspot = useCommonStore((state) => state.setActiveHostpot);

    const toggleMenu = () => {
        setShowMenu(!showMenu);
    };

    const menuRef = React.createRef<HTMLInputElement>();
    const menuButtonRef = React.createRef<HTMLInputElement>();

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target) && menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
            setShowMenu(false);
        }
    };

    useEffect(() => {
        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    });

    const handleDelete = async () => {
        if (hotspotId.length !== 0) {
            await deleteHotspot(hotspotId);
            let cmd = deleteHotspotConfig(hotspotId);
            const iFrameRef = IFrameRefHandler.getInstance('');
            iFrameRef.postMessage(cmd);
        } else await deleteFeedback(feedbackId);
        setToastMessage({ type: MessageType.SUCCESS, message: Messages.FEEDBACK_DELETED });
        const feedbackResponse = await getFeedback(activeProduct?.fsn);
        setFeedback(feedbackResponse.data);
        setFeedbackMessage('');
        setFlyoutRightOpen(false);
        setProductFlagged(true);
    };

    const handleResolve = async () => {
        await resolveFeedback(feedbackId);
        setToastMessage({ type: MessageType.SUCCESS, message: Messages.FEEDBACK_RESOLVED });
        const feedbackResponse = await getFeedback(activeProduct?.fsn);
        setFeedback(feedbackResponse.data);
        setFeedbackMessage('');
        setActiveHotspot('');
        setFlyoutRightOpen(false);
        setProductFlagged(true);
    };
    return (
        <div>
            <div className="flex flex-col w-52 h-50">
                <div className="flex justify-end">
                    <div
                        ref={menuButtonRef}
                        className={showMenu ? 'font-black w-8 h-8 rounded-lg bg-gray-200 cursor-pointer' : 'font-black  cursor-pointer bg-white hover:bg-gray-200 w-8 h-8 rounded-lg'}
                        onClick={toggleMenu}
                    >
                        <div className="pl-2">...</div>
                    </div>
                </div>
                {showMenu && (
                    <div ref={menuRef} className=" flex bg-white z-20 flex-col border-solid border-black rounded-lg w-52 h-50 ring-2 ring-gray-100 shadow-2xl mt-2">
                        <div className="m-1.5">
                            <div
                                className="flex items-center rounded-lg bg-white hover:bg-gray-200 w-full h-10 cursor-pointer"
                                onClick={() => {
                                    toggleMenu();
                                    handleDelete();
                                }}
                            >
                                <div className="pl-2">Delete</div>
                            </div>
                            {isResolved === false && (
                                <div
                                    className="flex items-center rounded-lg bg-white hover:bg-gray-200 w-full h-10 cursor-pointer"
                                    onClick={() => {
                                        toggleMenu();
                                        handleResolve();
                                    }}
                                >
                                    <div className="pl-2">Resolve</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ({ name, isResolved, dateAndtime, text, isHighlighted, feedbackId, hotspotId }: BannerProps) => {
    const user = useAuthStore((state) => state.user);
    const role = useAuthStore((state) => state.activeRole);
    const activeProduct = useRequestorStore((state) => state.activeProduct);
    const setFlyoutRightOpen = useCommonStore((state) => state.setFlyoutRightOpen);

    const shouldShowToolbaar = () => {
        if (role === Roles.ADMIN) return true;
        if (user.username === name) return true;
        return false;
    };

    const getCommentTimeStampDetails = (dateAndtime: string) => {
        let hours = Number(dateAndtime);
        if (hours > 24) return parseInt((hours / 24).toString()) + ' days ago';
        if (hours < 1) return parseInt((hours * 60).toString()) + ' min ago';

        return parseInt(hours.toString()) + ' hours ago';
    };

    const handleClick = async () => {
        let response = await fetchHotspotData(activeProduct?.fsn as string, hotspotId as string);
        let data: IhostspotResponse = response.data;
        let cmd = viewHotspotConfig(data);
        const iFrameRef = IFrameRefHandler.getInstance('');
        iFrameRef.postMessage(cmd);
        setFlyoutRightOpen(false);
    };

    return (
        <div className="py-2 flex-1 relative">
            <div className="flex justify-between w-full absolute">
                <div className={isHighlighted === false ? 'whitespace-no-wrap text-base mb-2 text-black-400' : 'whitespace-no-wrap text-gray-300 not-italic font-normal  text-sm leading-5'}>
                    {name} <span className="text-gray-300"> • </span> <span className="text-gray-300 not-italic font-normal  text-sm leading-5">{getCommentTimeStampDetails(dateAndtime as string)}</span>
                </div>
                {shouldShowToolbaar() && <ToolBar feedbackId={feedbackId} isResolved={isResolved} hotspotId={hotspotId} />}
            </div>
            <div
                className={
                    isHighlighted === false
                        ? 'whitespace-no-wrap text-gray-400 not-italic font-normal  text-sm leading-5 flex-item-center flex-none order-1 mt-7 mb-3 rounded-sm'
                        : 'whitespace-no-wrap text-gray-300 not-italic font-normal  text-sm leading-5 flex-item-center flex-none order-1   mt-7 mb-3 '
                }
            >
                {`${text}`}
            </div>
            {hotspotId ? (
                <div className="whitespace-no-wrap text-gray-500 not-italic font-normal  text-medium leading-5 border-solid border-b-2 border-x-gray-9000 border-opacity-100 cursor-pointer" onClick={handleClick}>
                    {hotspotId ? 'View highlight >' : ''}
                </div>
            ) : (
                <div className="border-solid	 border-b-2 border-x-gray-9000 border-opacity-100"> </div>
            )}
        </div>
    );
};
